// Provides declarative form submission and confirmation behavior shared by pages.
import { throwFrontendError } from '../core/errors'
import { requestConfirmation } from '../core/confirmation'
import { Log } from '../core/logger'

const initializedAutoSubmitForms = new WeakSet<HTMLFormElement>()
const initializedConfirmForms = new WeakSet<HTMLFormElement>()
const confirmedSubmissions = new WeakSet<HTMLFormElement>()

type HtmxConfirmationDetail = {
  issueRequest: (skipConfirmation?: boolean) => void
}

/**
 * Returns the form's own confirmation text; a destructive form without one is a page defect, never a generic question.
 */
function confirmationMessage(form: HTMLFormElement): string {
  const message = form.dataset.confirm?.trim() ?? ''

  if (message === '') {
    throwFrontendError(new Error(`Confirmation form ${form.action} has no data-confirm message.`))
  }

  return message
}

/**
 * Returns whether HTMX, rather than the browser, owns this form submission.
 */
function usesHtmx(form: HTMLFormElement): boolean {
  return Array.from(form.attributes).some((attribute) => attribute.name.startsWith('hx-'))
}

/**
 * Prevents an HTMX request until the shared application dialog is accepted.
 */
function initializeHtmxConfirmation(form: HTMLFormElement): void {
  form.addEventListener('htmx:confirm', async (event) => {
    await Log.info('Confirm asynchronous form request', async () => {
      const confirmationEvent = event as CustomEvent<HtmxConfirmationDetail>
      const issueRequest = confirmationEvent.detail?.issueRequest

      if (typeof issueRequest !== 'function') {
        throw new Error('HTMX confirmation event did not provide a request callback.')
      }

      event.preventDefault()

      const message = confirmationMessage(form)
      const accepted = await requestConfirmation(message)

      if (!accepted || !form.isConnected) {
        return
      }

      issueRequest(true)
    })
  })
}

/**
 * Discovers the relevant controls and binds the event handlers owned by the forms module.
 */
export function initializeAutoSubmitForms(root: ParentNode = document): void {
  root.querySelectorAll<HTMLFormElement>('form[data-auto-submit]').forEach((form) => {
    if (initializedAutoSubmitForms.has(form)) {
      return
    }

    initializedAutoSubmitForms.add(form)

    form.addEventListener('change', (event) => {
      if (event.target instanceof HTMLSelectElement || event.target instanceof HTMLInputElement) {
        Log.notice('Submit changed filter form', () => form.requestSubmit())
      }
    })
  })
}

/**
 * Discovers the relevant controls and binds the event handlers owned by the forms module.
 */
export function initializeConfirmForms(root: ParentNode = document): void {
  root.querySelectorAll<HTMLFormElement>('form[data-confirm]').forEach((form) => {
    if (initializedConfirmForms.has(form)) {
      return
    }

    initializedConfirmForms.add(form)

    if (usesHtmx(form)) {
      initializeHtmxConfirmation(form)

      return
    }

    form.addEventListener('submit', async (event) => {
      await Log.info('Confirm form submission', async () => {
        if (confirmedSubmissions.has(form)) {
          confirmedSubmissions.delete(form)

          return
        }

        event.preventDefault()

        const message = confirmationMessage(form)
        const accepted = await requestConfirmation(message)

        if (!accepted || !form.isConnected) {
          return
        }

        confirmedSubmissions.add(form)

        if (
          event.submitter instanceof HTMLButtonElement
          || event.submitter instanceof HTMLInputElement
        ) {
          form.requestSubmit(event.submitter)

          return
        }

        form.requestSubmit()
      })
    })
  })
}
